import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  GET_DASHBOARD_MODELS,
  GET_DASHBOARD_USER,
  GET_USER_NAMES,
} from "../endpoint";
import apiCall from "../utils/apiCall";
import { Search } from "@mui/icons-material";
import coins_svg from "../assets/coins.svg";
import selectuser_svg from "../assets/selectuser.svg";
import website_svg from "../assets/website.svg";
import token_svg from "../assets/token.svg";
import UserHeaderComponent from "./UserHeaderComponent";
import BarChart from "./BarGraph";
import DoughnutChart from "./DoughnutChart";
import RequestsHeader from "./RequestHeader";
import RequestsItem from "./RequestItem";
import LoadingDashboard from "./LoadingDashboard";
import most_svg from "../assets/most.svg";
import less_svg from "../assets/less.svg";
import model_supported_svg from "../assets/aimodel.svg";
import wallet_svg from "../assets/wallet.svg";

const Models: React.FunctionComponent = () => {
  const [userData, setUserData] = React.useState<any>();
  const [loading, setLoading] = React.useState(false);
  
  React.useEffect(() => {
    const fetchModels = () => {
      setLoading(true);
      apiCall("GET", GET_DASHBOARD_MODELS)
        .then((res: any) => {
          const modelMetrics: { [key: string]: any } = {};
          let total_cost = 0;
          let total_tokens = 0;
          let total_calls = 0;

          // Process each model's data
          Object.entries(res.result).forEach(([modelName, chats]: [string, any]) => {
            modelMetrics[modelName] = {
              cost_default: 0,
              cost_rag: 0,
              tokens_default: 0,
              tokens_rag: 0,
              total_calls: chats.length
            };

            chats.forEach((chat: any) => {
              const isDefault = chat.chat_type === "default";
              const metrics = modelMetrics[modelName];
              
              if (isDefault) {
                metrics.cost_default += chat.cost || 0;
                metrics.tokens_default += chat.total_token || 0;
              } else {
                metrics.cost_rag += chat.cost || 0;
                metrics.tokens_rag += chat.total_token || 0;
              }

              total_cost += chat.cost || 0;
              total_tokens += chat.total_token || 0;
            });

            total_calls += chats.length;
          });

          // Create sorted arrays for analysis
          // Validate and transform metrics to ensure non-negative values
          const modelAnalytics = Object.entries(modelMetrics).map(([name, metrics]) => {
            const totalCalls = Math.max(0, metrics.total_calls || 0);
            const totalCost = Math.max(0, (metrics.cost_default || 0) + (metrics.cost_rag || 0));
            const totalTokens = Math.max(0, (metrics.tokens_default || 0) + (metrics.tokens_rag || 0));
            
            return [name, totalCalls, totalCost, totalTokens];
          });

          // Ensure we have valid data before sorting
          const defaultModel = ["No Model", 0, 0, 0];
          let sort_by_count = [defaultModel];
          let sort_by_cost = [defaultModel];
          let sort_by_tokens = [defaultModel];

          if (modelAnalytics.length > 0) {
            // Sort in descending order for most used (highest to lowest)
            sort_by_count = [...modelAnalytics].sort((a: any, b: any) => {
              const countA = Math.max(0, a[1] || 0);
              const countB = Math.max(0, b[1] || 0);
              return countB - countA;
            });

            sort_by_cost = [...modelAnalytics].sort((a: any, b: any) => {
              const costA = Math.max(0, a[2] || 0);
              const costB = Math.max(0, b[2] || 0);
              return costB - costA;
            });

            sort_by_tokens = [...modelAnalytics].sort((a: any, b: any) => {
              const tokensA = Math.max(0, a[3] || 0);
              const tokensB = Math.max(0, b[3] || 0);
              return tokensB - tokensA;
            });
          }

          setUserData({
            model_count_labels: Object.keys(modelMetrics),
            model_count_values: Object.values(modelMetrics).map(m => m.total_calls),
            total_cost: Math.max(0, total_cost),
            total_tokens: Math.max(0, total_tokens),
            total_calls: Math.max(0, total_calls),
            ...Object.entries(modelMetrics).reduce((acc, [name, metrics]) => ({
              ...acc,
              [`${name.toLowerCase().replace(/-/g, '_')}_cost_default`]: metrics.cost_default,
              [`${name.toLowerCase().replace(/-/g, '_')}_cost_rag`]: metrics.cost_rag,
              [`${name.toLowerCase().replace(/-/g, '_')}_tokens_default`]: metrics.tokens_default,
              [`${name.toLowerCase().replace(/-/g, '_')}_tokens_rag`]: metrics.tokens_rag,
            }), {}),
            mostusedbycount: sort_by_count[0] || defaultModel,
            leastusedbycount: sort_by_count[sort_by_count.length - 1] || defaultModel,
            mostusedbycost: sort_by_cost[0] || defaultModel,
            leastusedbycost: sort_by_cost[sort_by_cost.length - 1] || defaultModel,
            mostusedbytokens: sort_by_tokens[0] || defaultModel,
            leastusedbytokens: sort_by_tokens[sort_by_tokens.length - 1] || defaultModel,
          });
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => setLoading(false));
    };
    fetchModels();
  }, []);
  return (
    <Grid
      container
      sx={{
        my: 2,
      }}
      direction="column"
    >
      {loading ? (
        <LoadingDashboard />
      ) : (
        <>
          <Grid container>
            <Grid container xs={6} direction="column" alignItems="center">
              <Grid
                sx={{
                  width: "90%",
                  pb: 4,
                }}
              >
                <UserHeaderComponent
                  title="Most used model"
                  value={userData?.mostusedbycount ? `${userData.mostusedbycount[0]} (${userData.mostusedbycount[1]} uses)` : "No data available"}
                  icon={most_svg}
                  xs={12}
                />
                <Grid sx={{ mb: 3 }} />
                <UserHeaderComponent
                  title="Least used model"
                  value={userData?.leastusedbycount ? `${userData.leastusedbycount[0]} (${userData.leastusedbycount[1]} uses)` : "No data available"}
                  icon={less_svg}
                  xs={12}
                />
                <Grid sx={{ mb: 3 }} />
                <UserHeaderComponent
                  title="Models supported"
                  value={userData?.model_count_labels.length}
                  icon={model_supported_svg}
                  xs={12}
                />
              </Grid>
            </Grid>
            <Grid container xs={6} direction="column" alignItems="center">
              <Grid
                sx={{
                  height: "200px",
                  width: "90%",
                  marginTop: -5,
                }}
              >
                <DoughnutChart
                  labels={userData?.model_count_labels}
                  dataset={userData?.model_count_values}
                />
                <Divider
                  sx={{
                    mt: 2,
                  }}
                >
                  Cost for chat types for each model
                </Divider>
              </Grid>
            </Grid>
          </Grid>
          <Grid sx={{ mb: 5 }} />
          <Divider />
          <Grid
            container
            sx={{
              mt: 2,
            }}
          >
            <Typography>Most spending</Typography> &nbsp;&nbsp;&nbsp;
            <Typography color="secondary">Most spending</Typography>
          </Grid>
          <Grid container my={2} alignItems="center">
            <UserHeaderComponent
              title="Total Cost:"
              value={userData?.total_cost}
              icon={coins_svg}
            />
            <UserHeaderComponent
              title="Total Events:"
              value={userData?.total_calls}
              icon={website_svg}
            />
            <UserHeaderComponent
              title="Total tokens:"
              value={userData?.total_tokens}
              icon={token_svg}
            />
          </Grid>
          <Divider
            sx={{
              mb: 2,
            }}
          />
          <Grid container>
            <Grid
              container
              xs={6}
              sx={{
                borderRight: "1px solid rgba(0,0,0,.1)",
                // p: 2,
              }}
              direction="column"
              alignItems="center"
            >
              <Grid
                sx={{
                  height: "250px",
                  width: "90%",
                }}
              >
                {userData && (
                  <BarChart
                    labels={userData.model_count_labels}
                    dataset2={userData.model_count_labels.map(
                      (model: string) =>
                        userData[`${model.toLowerCase().replace(/-/g, "_")}_cost_rag`] || 0
                    )}
                    label1="Default"
                    dataset1={userData.model_count_labels.map(
                      (model: string) =>
                        userData[`${model.toLowerCase().replace(/-/g, "_")}_cost_default`] || 0
                    )}
                    label2="RAG"
                  />
                )}
                <Divider
                  sx={{
                    mt: 2,
                  }}
                >
                  Cost for chat types for each model
                </Divider>
              </Grid>
            </Grid>
            <Grid container xs={6} direction="column" alignItems="center">
              <Grid
                sx={{
                  height: "250px",
                  width: "90%",
                }}
              >
                {userData && (
                  <BarChart
                    labels={userData.model_count_labels}
                    dataset2={userData.model_count_labels.map(
                      (model: string) =>
                        userData[`${model.toLowerCase().replace(/-/g, "_")}_tokens_rag`] || 0
                    )}
                    label1="Default"
                    dataset1={userData.model_count_labels.map(
                      (model: string) =>
                        userData[`${model.toLowerCase().replace(/-/g, "_")}_tokens_default`] || 0
                    )}
                    label2="RAG"
                  />
                )}
                <Divider
                  sx={{
                    mt: 2,
                  }}
                >
                  Cost for chat types for each model
                </Divider>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            sx={{
              my: 4,
            }}
          />
          {/* <RequestsHeader /> */}
          {/* {userData?.chats.map((item: any) => (
            <RequestsItem item={item} key={item.chat_id} />
          ))} */}
        </>
      )}
    </Grid>
  );
};

export default Models;
